import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import stateCityData from '../../data/state_city.json';
import './CitySelect.css';

const CitySelect = ({ value, onChange, stateValue, stateMatchField = 'stateCode', className = '', disabled = false }) => {
  const cityOptions = useMemo(() => {
    if (!stateValue) return [];

    const stateData = stateCityData.find(
      (item) => item[stateMatchField] === stateValue
    );

    if (stateData && stateData.cities) {
      return stateData.cities.split(',').map(city => ({
        value: city.trim(),
        label: city.trim()
      }));
    }

    return [];
  }, [stateValue, stateMatchField]);

  const selectedOption = value ? { value, label: value } : null;

  const noState = !stateValue;

  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: state.isDisabled ? '#f3f4f6' : '#f9fafb',
      border: state.isFocused ? '2px solid #5b4ba8' : '2px solid transparent',
      borderRadius: '16px',
      padding: '8px 12px',
      fontSize: '15px',
      fontFamily: "'Inter', sans-serif",
      boxShadow: state.isFocused ? '0 0 0 4px rgba(91, 75, 168, 0.1)' : 'none',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      minHeight: '52px',
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: state.isFocused ? '#5b4ba8' : '#d1d5db',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#2B1055',
      fontSize: '15px',
      fontFamily: "'Inter', sans-serif",
    }),
    input: (base) => ({
      ...base,
      color: '#2B1055',
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      zIndex: 10,
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#5b4ba8'
        : state.isFocused
        ? 'rgba(91, 75, 168, 0.08)'
        : 'transparent',
      color: state.isSelected ? '#fff' : '#1f2937',
      borderRadius: '8px',
      padding: '10px 12px',
      fontSize: '14px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'rgba(91, 75, 168, 0.15)',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? '#5b4ba8' : '#9ca3af',
      '&:hover': {
        color: '#5b4ba8',
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#9ca3af',
      '&:hover': {
        color: '#ef4444',
      },
    }),
  };

  return (
    <div className={`city-select-wrapper ${className}`}>
      <CreatableSelect
        styles={customStyles}
        value={selectedOption}
        onChange={(option) => onChange(option ? option.value : '')}
        options={cityOptions}
        isDisabled={disabled || noState}
        isClearable
        placeholder={noState ? 'Select state first' : 'Search or type a city...'}
        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
        noOptionsMessage={() => noState ? 'Select a state first' : 'No cities found — type to add'}
      />
    </div>
  );
};

export default CitySelect;
